import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { parseApiError } from "../../lib/errors";
import { PrimaryButton, QuietButton } from "../UI";
import { ActionChip } from "./ImportRowsTable";

/* LT-19d — edit one preview row before Apply. Every contract column is
 * editable; category / unit / coverage unit / supplier are dropdowns from
 * Settings so the fix can't be a typo. Save PATCHes the batch's row edits
 * and the server re-checks the row (and the batch, for duplicates). The
 * original file is never rewritten. */

const FIELDS = [
  { col: "sku", label: "SKU", hint: "blank = create a new material" },
  { col: "name", label: "Name", required: true, wide: true },
  { col: "category", label: "Category", kind: "category", required: true },
  { col: "unit", label: "Unit (bought as)", kind: "unit", required: true },
  { col: "current_price", label: "Current price £", kind: "number", required: true },
  { col: "coverage_per_unit", label: "Coverage per unit", kind: "number", hint: "how much one purchase unit covers" },
  { col: "coverage_unit", label: "Coverage unit", kind: "unit", hint: "what one purchase unit covers — the same unit as above means 1:1 (nothing to convert)" },
  { col: "default_wastage_pct", label: "Default wastage %", kind: "number" },
  { col: "stock_length", label: "Stock length", kind: "number", hint: "divisible units only (a length that is cut)" },
  { col: "supplier_name", label: "Supplier", kind: "supplier" },
  { col: "supplier_ref", label: "Supplier ref" },
  { col: "supplier_price", label: "Supplier price £", kind: "number" },
  { col: "keywords", label: "Keywords", hint: "semicolon separated", wide: true },
  { col: "notes", label: "Notes", wide: true },
];

const inputCls = "w-full h-8 px-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent";

// Rows resolved before LT-19d carry no `cells`; derive them from the raw file row.
const cellsOf = (row) => row?.cells
  || Object.fromEntries(Object.entries(row?.raw || {}).map(([k, v]) => [k.trim().toLowerCase().replace(/\s+/g, "_"), v]));

export function ImportRowEditDialog({ batchId, row, open, onClose, onSaved }) {
  const [draft, setDraft] = useState({});
  const [lookups, setLookups] = useState({ categories: [], units: [], suppliers: [] });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const base = useMemo(() => cellsOf(row), [row]);

  useEffect(() => {
    if (!open || !row) return;
    setDraft({ ...base });
    setError(null);
    Promise.all([
      api.get("/material-categories"), api.get("/material-units"), api.get("/suppliers"),
    ]).then(([c, u, s]) => setLookups({ categories: c.data || [], units: u.data || [], suppliers: s.data || [] }))
      .catch(() => {});
  }, [open, row, base]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const changed = useMemo(() => {
    const out = {};
    for (const f of FIELDS) {
      const v = (draft[f.col] ?? "").toString();
      if (v !== (base[f.col] ?? "").toString()) out[f.col] = v;
    }
    return out;
  }, [draft, base]);

  if (!open || !row) return null;

  const unitOptions = lookups.units.filter((u) => u.status !== "archived");
  const optionsFor = (f) => {
    if (f.kind === "category") return lookups.categories.filter((c) => c.status !== "archived").map((c) => ({ label: c.label, key: c.key, aliases: c.aliases }));
    if (f.kind === "unit") return unitOptions.map((u) => ({ label: u.label, key: u.key, aliases: u.aliases }));
    if (f.kind === "supplier") return lookups.suppliers.filter((s) => s.status !== "archived").map((s) => ({ label: s.name, key: s.key }));
    return null;
  };
  // The file may say "m2" or "metre" where Settings says "m²" / "m" (the server
  // matches key, label OR alias, case-insensitively) — show that as the Settings
  // option, not as unknown.
  const norm = (s) => (s ?? "").toString().trim().toLowerCase().replace(/\s+/g, " ");
  const matchOption = (opts, v) => opts.find((o) => norm(o.label) === norm(v) || (o.key && norm(o.key) === norm(v))
    || (o.aliases || []).some((a) => norm(a) === norm(v)));
  const unitIsDivisible = (label) => !!unitOptions.find((u) => u.label === label)?.is_divisible;

  const save = async () => {
    if (!Object.keys(changed).length) { onClose(); return; }
    setBusy("save"); setError(null);
    try {
      const r = await api.patch(`/material-imports/${batchId}/rows/${row.row_number}`, { cells: changed });
      const nr = r.data.row;
      if (nr.action === "reject") toast.warning(`Row ${row.row_number} re-checked — still rejected: ${nr.reject_reason}`);
      else toast.success(`Row ${row.row_number} re-checked → ${nr.action.toUpperCase()}`);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      setError(parseApiError(e).message);
    } finally { setBusy(null); }
  };
  const reset = async () => {
    setBusy("reset"); setError(null);
    try {
      const r = await api.patch(`/material-imports/${batchId}/rows/${row.row_number}`, { reset: true });
      toast.success(`Row ${row.row_number} reset to the file's values`);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      setError(parseApiError(e).message);
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-start justify-center pt-12 px-4" onClick={onClose} data-testid="import-row-edit-backdrop">
      <div className="w-full max-w-3xl max-h-[calc(100vh-96px)] flex flex-col bg-surface border border-line rounded-lg shadow-card overflow-hidden"
        onClick={(e) => e.stopPropagation()} data-testid="import-row-edit-dialog">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-line">
          <div className="min-w-0">
            <div className="col-head text-ink-3" style={{ fontSize: 9.5 }}>Edit before apply · row {row.row_number}</div>
            <h2 className="font-display text-ink truncate" style={{ fontSize: 16, fontWeight: 600 }} data-testid="import-row-edit-title">
              {base.name || "Untitled row"}
            </h2>
            <div className="mt-1 flex items-center gap-2" style={{ fontSize: 12 }}>
              <ActionChip action={row.action} />
              {row.edited && <span className="text-amber-800 font-mono uppercase" style={{ fontSize: 10, letterSpacing: 0.4 }} data-testid="import-row-edit-edited-marker">edited</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-ink-3 hover:text-ink hover:bg-surface-2" aria-label="Close" data-testid="import-row-edit-close">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto scrollbar-thin flex-1 px-5 py-4 space-y-4">
          {row.reject_reason && (
            <div className="rounded border border-red-200 bg-red-50 text-red-900 px-3 py-2 flex gap-2" style={{ fontSize: 12 }} data-testid="import-row-edit-reason">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{row.reject_reason}</span>
            </div>
          )}
          {(row.warnings || []).map((w, i) => (
            <div key={i} className="rounded border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2" style={{ fontSize: 12 }}>{w}</div>
          ))}
          <div className="grid grid-cols-3 gap-3">
            {FIELDS.map((f) => {
              const opts = optionsFor(f);
              const v = draft[f.col] ?? "";
              const orig = (row.original_raw ? Object.entries(row.original_raw).find(([k]) => k.trim().toLowerCase() === f.col)?.[1] : null);
              const wasEdited = row.edited_cells && f.col in row.edited_cells;
              const dirty = f.col in changed;
              return (
                <label key={f.col} className={f.wide ? "col-span-3 flex flex-col gap-1" : "flex flex-col gap-1"}>
                  <span className="flex items-center justify-between">
                    <span className="col-head text-ink-3" style={{ fontSize: 9.5 }}>
                      {f.label}{f.required && <span className="text-note-red ml-0.5">*</span>}
                    </span>
                    {(wasEdited || dirty) && (
                      <span className="text-amber-800 font-mono" style={{ fontSize: 9.5 }} data-testid={`import-row-edit-${f.col}-flag`}>
                        {dirty ? "changed" : `edited · file had "${orig ?? ""}"`}
                      </span>
                    )}
                  </span>
                  {opts ? (() => {
                    const hit = v ? matchOption(opts, v) : null;
                    const selectValue = hit ? hit.label : v;
                    return (
                      <select value={selectValue} onChange={(e) => setDraft((d) => ({ ...d, [f.col]: e.target.value }))}
                        className={inputCls} style={{ fontSize: 12.5 }} data-testid={`import-row-edit-${f.col}`}>
                        <option value="">{f.required ? "— choose —" : "— none —"}</option>
                        {v && !hit && <option value={v}>{v} (not in Settings)</option>}
                        {opts.map((o) => (
                          <option key={o.label} value={o.label}>
                            {o.label}{f.kind === "unit" && f.col === "unit" ? (unitIsDivisible(o.label) ? " · divisible" : " · bought whole") : ""}
                          </option>
                        ))}
                      </select>
                    );
                  })() : (
                    <input value={v} onChange={(e) => setDraft((d) => ({ ...d, [f.col]: e.target.value }))}
                      inputMode={f.kind === "number" ? "decimal" : undefined}
                      className={inputCls + (f.kind === "number" ? " font-mono tnum text-right" : "")} style={{ fontSize: 12.5 }}
                      data-testid={`import-row-edit-${f.col}`} />
                  )}
                  {f.hint && <span className="text-ink-4" style={{ fontSize: 10.5 }}>{f.hint}</span>}
                </label>
              );
            })}
          </div>
          {error && <div className="rounded border border-red-200 bg-red-50 text-red-900 px-3 py-2" style={{ fontSize: 12 }} data-testid="import-row-edit-error">{error}</div>}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line bg-surface">
          <div className="text-ink-4" style={{ fontSize: 11 }}>
            Edits live on this import only — the original file is kept as uploaded. Download the revised CSV to keep them for next time.
          </div>
          <div className="flex items-center gap-2">
            {row.edited && (
              <QuietButton icon={busy === "reset" ? Loader2 : RotateCcw} onClick={reset} disabled={!!busy} data-testid="import-row-edit-reset">Reset to file</QuietButton>
            )}
            <QuietButton onClick={onClose} disabled={!!busy} data-testid="import-row-edit-cancel">Cancel</QuietButton>
            <PrimaryButton icon={busy === "save" ? Loader2 : undefined} onClick={save} disabled={!!busy || !Object.keys(changed).length}
              className="whitespace-nowrap" data-testid="import-row-edit-save">
              Save & re-check
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
